/**
 * PWA + Web Push helpers.
 *
 * Registers the service worker (which enables "Add to Home Screen") and
 * provides a one-call subscribe/unsubscribe API for browser push.
 */
import { api } from "./api";

let registrationPromise = null;

/**
 * Register the service worker. Idempotent — safe to call multiple times.
 * Returns the active ServiceWorkerRegistration or null if unsupported.
 */
export function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return Promise.resolve(null);
  }
  if (registrationPromise) return registrationPromise;
  registrationPromise = navigator.serviceWorker
    .register("/sw.js", { scope: "/" })
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.warn("SW registration failed:", e);
      return null;
    });
  return registrationPromise;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function notificationPermission() {
  return typeof Notification !== "undefined" ? Notification.permission : "denied";
}

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Get the current push subscription if one exists for this device.
 */
export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const reg = await registerServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Request notification permission and subscribe the user to web push.
 * Returns the active PushSubscription on success, throws on failure so the
 * caller can show an error toast.
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error("Tavo naršyklė nepalaiko push pranešimų");
  }
  const reg = await registerServiceWorker();
  if (!reg) throw new Error("Service worker neįsiregistravo");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Pranešimai neleisti");
  }

  // Fetch the VAPID public key from the backend so the same key is reused
  // across deploys — we never hardcode it in the JS bundle.
  const vapidRes = await api.get("/push/vapid-public-key");
  const publicKey = vapidRes.data.key;
  if (!publicKey) throw new Error("Push serveris nesukonfigūruotas");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  // Persist on the backend so it can target this device later.
  await api.post("/push/subscribe", sub.toJSON());
  return sub;
}

/**
 * Unsubscribe this device from push.
 */
export async function unsubscribeFromPush() {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch (_e) {/* server may not know us, that's fine */}
  await sub.unsubscribe();
}
